import React, { useEffect, useMemo, useState } from 'react';
import { Card, Input, List, Tag, Tooltip, Typography, Collapse, Space, Empty } from 'antd';
import { QuestionCircleOutlined, RightOutlined, LockOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { adminPath } from '../../../shared/config/paths';
import systemService from '../../services/systemService';
import cdnService from '../../services/cdnService';
import { SETTINGS, SETTING_GROUPS, ENV_HINT, matchSetting } from './settingsRegistry';

const { Text, Title } = Typography;

// 把 cdn global-settings（按分类分组）摊平成 { key: value }
const flattenCdn = (grouped) => {
  const out = {};
  Object.values(grouped || {}).forEach((items) => {
    Object.entries(items || {}).forEach(([k, v]) => { out[k] = v ? v.value : undefined; });
  });
  return out;
};

const showValue = (s, v) => {
  if (v === undefined) return null;
  if (s.type === 'bool' || typeof v === 'boolean') {
    const on = v === true || v === 1 || v === '1' || v === 'true';
    return <Tag color={on ? 'green' : 'default'}>{on ? '开' : '关'}</Tag>;
  }
  if (s.type === 'enum' && s.options) return <Tag>{s.options[v] || String(v)}</Tag>;
  if (Array.isArray(v)) return <Tag>{v.join(', ') || '空'}</Tag>;
  if (v === null || v === '') return <Tag>未设置</Tag>;
  if (typeof v === 'object') return <Tag>已配置</Tag>;
  const str = String(v);
  return <Tag>{str.length > 32 ? str.slice(0, 32) + '…' : str}</Tag>;
};

const SettingsHub = () => {
  const [q, setQ] = useState('');
  const [values, setValues] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      const next = {};
      try {
        const r = await systemService.getRuntimeSettings();
        if (r && r.success) Object.entries(r.data || {}).forEach(([k, v]) => { next[k] = v.value; next['__raw_' + k] = v.raw; });
      } catch (_) { /* 只读展示，失败不影响导航 */ }
      try {
        const r = await cdnService.getGlobalSettings();
        if (r && r.success) Object.assign(next, flattenCdn(r.data));
      } catch (_) { /* ignore */ }
      try {
        const r = await systemService.getRateLimitRules();
        if (r && r.success) next['rate_limit.enabled'] = r.data?.enabled ?? true;
      } catch (_) { /* ignore */ }
      if (alive) setValues(next);
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => SETTINGS.filter((s) => matchSetting(s, q)), [q]);

  const renderItem = (s) => {
    const overridden = s.envOverride && values['__raw_' + s.envOverride];
    const v = s.source === 'page' ? undefined : values[s.key];
    const body = (
      <List.Item
        style={{ padding: '10px 4px' }}
        extra={s.path ? <RightOutlined style={{ color: '#bbb' }} /> : null}
      >
        <Space size={6} wrap style={{ flex: 1 }}>
          <Text strong>{s.label}</Text>
          <Tooltip title={<span>{s.help}<br /><Text code style={{ color: 'inherit' }}>{s.key}</Text></span>}>
            <QuestionCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
          {s.danger && <Tag color="red">谨慎</Tag>}
          {s.source === 'env' && (
            <Tooltip title={ENV_HINT}><Tag icon={<LockOutlined />}>环境变量</Tag></Tooltip>
          )}
          {overridden && (
            <Tooltip title={`被环境变量 ${s.envOverride} 覆盖，此处修改不生效`}><Tag color="orange">被 .env 覆盖</Tag></Tooltip>
          )}
        </Space>
        <span style={{ marginRight: 8 }}>{showValue(s, v)}</span>
      </List.Item>
    );
    return s.path ? <Link to={adminPath(s.path)} style={{ display: 'block', color: 'inherit' }}>{body}</Link> : body;
  };

  const groupCard = (g, items) => (
    <Card key={g.key} size="small" title={g.title} style={{ marginBottom: 16 }}>
      <List size="small" dataSource={items} renderItem={renderItem} rowKey="key" />
    </Card>
  );

  const searching = q.trim() !== '';
  const groups = SETTING_GROUPS.map((g) => ({ g, items: filtered.filter((s) => s.group === g.key) }))
    .filter(({ items }) => items.length > 0);

  return (
    <div style={{ maxWidth: 900 }}>
      <Title level={3} style={{ marginTop: 0 }}>系统设置</Title>
      <Input.Search
        placeholder="搜索设置（名称 / 说明 / 键名）"
        allowClear
        size="large"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      {groups.length === 0 && <Empty description="没有匹配的设置" />}
      {groups.map(({ g, items }) => {
        // 普通项直接列出；标了 advanced 的项（含整个「高级」组）收进折叠面板，搜索时全部展开
        const common = items.filter((s) => !s.advanced && !g.advanced);
        const adv = items.filter((s) => s.advanced || g.advanced);
        if (searching) return groupCard(g, items);
        return (
          <Card key={g.key} size="small" title={g.title} style={{ marginBottom: 16 }}>
            {common.length > 0 && <List size="small" dataSource={common} renderItem={renderItem} rowKey="key" />}
            {adv.length > 0 && (
              <Collapse ghost items={[{ key: 'adv', label: <Text type="secondary">{g.advanced ? '展开' : `高级（${adv.length}）`}</Text>, children: <List size="small" dataSource={adv} renderItem={renderItem} rowKey="key" /> }]} />
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default SettingsHub;
